import { AlertCircle, RefreshCw, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PendingAction, formatActionDescription } from "@/lib/offline/action-queue";
import { format } from "date-fns";
import { fi } from "date-fns/locale";

interface FailedActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  failedActions: PendingAction[];
  onRetry: (actionId: string) => void;
  onDiscard: (actionId: string) => void;
}

export function FailedActionsDialog({
  open,
  onOpenChange,
  failedActions,
  onRetry,
  onDiscard,
}: FailedActionsDialogProps) {
  if (failedActions.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Epäonnistuneet toiminnot
          </DialogTitle>
          <DialogDescription>
            Nämä toiminnot eivät onnistuneet useiden yritysten jälkeen. 
            Voit yrittää uudelleen tai hylätä ne.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[300px]">
          <div className="space-y-3">
            {failedActions.map(action => (
              <div 
                key={action.id} 
                className="flex flex-col gap-2 p-3 rounded-lg border bg-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {formatActionDescription(action)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(action.timestamp, "d.M. HH:mm", { locale: fi })}
                    </p>
                  </div>
                </div>
                
                {action.lastError && (
                  <p className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                    {action.lastError}
                  </p>
                )}

                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDiscard(action.id)}
                    className="gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Hylkää
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onRetry(action.id)}
                    className="gap-1"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Yritä uudelleen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
